import { CAMERA } from '@/constants/object';
import CameraControls from 'camera-controls';
import * as THREE from 'three';

CameraControls.install({ THREE: THREE });

// Cameras
export const perspectiveCamera = new THREE.PerspectiveCamera(
  CAMERA.PERSPECTIVE_CAMERA_FOV,
  CAMERA.PERSPECTIVE_CAMERA_ASPECT,
  CAMERA.CAMERA_NEAR,
  CAMERA.CAMERA_FAR
);
perspectiveCamera.position.set(
  CAMERA.DEFAULT_CAMERA_POSITION.x,
  CAMERA.DEFAULT_CAMERA_POSITION.y,
  CAMERA.DEFAULT_CAMERA_POSITION.z
);

export const orthographicCamera = new THREE.OrthographicCamera(
  CAMERA.ORTHO_CAMERA_LEFT,
  CAMERA.ORTHO_CAMERA_RIGHT,
  CAMERA.ORTHO_CAMERA_TOP,
  CAMERA.ORTHO_CAMERA_BOTTOM,
  CAMERA.ORTHO_CAMERA_NEAR,
  CAMERA.CAMERA_FAR
);
orthographicCamera.position.set(
  CAMERA.DEFAULT_ORTHO_CAMERA_POSITION.x,
  CAMERA.DEFAULT_ORTHO_CAMERA_POSITION.y,
  CAMERA.DEFAULT_ORTHO_CAMERA_POSITION.z
);

// Active camera and controls
export let camera = perspectiveCamera;
export let cameraControls = null;

function applyCameraControlSettings(cc) {
  cc.rotateSpeed = CAMERA.CAMERA_ROTATE_SPEED;
  cc.draggingSmoothTime = CAMERA.CAMERA_DRAGGING_SMOOTH_TIME;
  cc.dollySpeed = CAMERA.CAMERA_DOLLY_SPEED;
  cc.smoothTime = CAMERA.CAMERA_SMOOTH_TIME;
  cc.dollyToCursor = CAMERA.CAMERA_DOLLY_TO_CURSOR;
  cc.minDistance = CAMERA.CAMERA_MIN_DISTANCE;
  cc.maxDistance = CAMERA.CAMERA_MAX_DISTANCE;
  cc.minZoom = CAMERA.CAMERA_MIN_ZOOM;
  cc.maxZoom = CAMERA.CAMERA_MAX_ZOOM;
  cc.mouseButtons.left = CameraControls.ACTION.TRUCK;
  cc.mouseButtons.right = CameraControls.ACTION.ROTATE;
  cc.mouseButtons.wheel = cc.camera.isOrthographicCamera ? CameraControls.ACTION.ZOOM : CameraControls.ACTION.DOLLY;
  cc.touches.one = CameraControls.ACTION.TOUCH_TRUCK;
  cc.touches.two = CameraControls.ACTION.TOUCH_DOLLY_ROTATE;
}

// Init camera controls
export function initCameraControls(container) {
  if (!container) return console.error('Container is required');
  perspectiveCamera.near = CAMERA.CAMERA_NEAR;
  perspectiveCamera.far = CAMERA.CAMERA_FAR;
  perspectiveCamera.updateProjectionMatrix();
  cameraControls = new CameraControls(camera, container);

  applyCameraControlSettings(cameraControls);
  return cameraControls;
}

// Resize cameras
export function updateCameraAspect(container) {
  const width = container.clientWidth;
  const height = container.clientHeight;
  if (width === 0 || height === 0) return;

  // Perspective
  perspectiveCamera.aspect = width / height;
  perspectiveCamera.updateProjectionMatrix();

  // Ortho
  const aspect = width / height;
  orthographicCamera.left = (-CAMERA.ORTHO_CAMERA_FRUSTUM_SIZE * aspect) / 2;
  orthographicCamera.right = (CAMERA.ORTHO_CAMERA_FRUSTUM_SIZE * aspect) / 2;
  orthographicCamera.top = CAMERA.ORTHO_CAMERA_FRUSTUM_SIZE / 2;
  orthographicCamera.bottom = -CAMERA.ORTHO_CAMERA_FRUSTUM_SIZE / 2;
  orthographicCamera.updateProjectionMatrix();
}

// Dispose controls
export function cleanupCameraControls() {
  if (cameraControls) {
    cameraControls.dispose();
    cameraControls = null;
  }
}

// Camera switcher
export function switchCamera(container, toOrtho = false) {
  if (!cameraControls || !container) return;

  const pos = new THREE.Vector3();
  const target = new THREE.Vector3();
  const zoom = cameraControls._zoom;

  cameraControls.getPosition(pos);
  cameraControls.getTarget(target);

  cameraControls.dispose();

  camera = toOrtho ? orthographicCamera : perspectiveCamera;
  camera.position.copy(pos);

  cameraControls = new CameraControls(camera, container);
  cameraControls.setLookAt(pos.x, pos.y, pos.z, target.x, target.y, target.z, false);
  applyCameraControlSettings(cameraControls);
  cameraControls.zoomTo(zoom, true);

  // Update camera aspect ratio for the new camera
  updateCameraAspect(container);

  const event = new CustomEvent('cameraChanged', { detail: { camera } });
  window.dispatchEvent(event);
}

// Optional: helper to check which one is active
export function isOrthographic() {
  return camera instanceof THREE.OrthographicCamera;
}

// Reset camera to default position and zoom
export function resetCameraToDefault() {
  if (!cameraControls) return;

  const pos = isOrthographic() ? CAMERA.DEFAULT_ORTHO_CAMERA_POSITION : CAMERA.DEFAULT_CAMERA_POSITION;

  // Normalize accumulated rotation angles to [-PI, PI] so the animated
  // transition always takes the shortest arc rather than unwinding full turns.
  const TAU = Math.PI * 2;
  const wrapAngle = (a) => {
    const mod = ((a % TAU) + TAU) % TAU;
    return mod > Math.PI ? mod - TAU : mod;
  };
  cameraControls.azimuthAngle = wrapAngle(cameraControls.azimuthAngle);
  cameraControls.polarAngle = wrapAngle(cameraControls.polarAngle);

  cameraControls.setLookAt(
    pos.x,
    pos.y,
    pos.z,
    CAMERA.DEFAULT_CAMERA_TARGET.x,
    CAMERA.DEFAULT_CAMERA_TARGET.y,
    CAMERA.DEFAULT_CAMERA_TARGET.z,
    true
  );
  cameraControls.zoomTo(CAMERA.DEFAULT_CAMERA_ZOOM, true);
}
